import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";
import LicenseTabs from "@/presentation/license/licenseTabs";
import routes from "@/router/routes";
import { Check } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export enum LicensePrincingCardVariants {
    Default,
    Compact,
}

interface PriceProps {
    price: number;
    enterprise?: boolean;
    className?: string;
}

const Price = ({ price, enterprise = false, className }: PriceProps) => {
    const { t } = useTranslation();
    const { language } = i18n;

    const formattedPrice = new Intl.NumberFormat(language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(price / 100);

    return (
        <span
            className={cn(
                "min-w-fit bg-gradient-to-l from-[#925AFF] to-[#43006C] bg-clip-text text-xl font-bold text-transparent",
                className,
            )}
        >
            {enterprise && (
                <span className="text-sm">{t("license.price.from")}</span>
            )}
            {t("license.price.format", { price: formattedPrice })}
            {enterprise && (
                <span className="text-sm">{t("license.price.perPerson")}</span>
            )}
        </span>
    );
};

interface LicensePrincingCardProps {
    buttonText: string;
    description: string;
    features?: string[];
    onClick?: () => void;
    price?: number;
    disabled?: boolean;
    title: string;
    variant?: LicensePrincingCardVariants;
    licenseTab?: LicenseTabs;
    isCurrent?: boolean;
}

const LicensePrincingCard: React.FC<LicensePrincingCardProps> = ({
    isCurrent,
    buttonText,
    description,
    features,
    onClick,
    price,
    disabled = false,
    title,
    variant = LicensePrincingCardVariants.Default,
    licenseTab = LicenseTabs.PUBLIC,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <Card
            className={cn(
                "relative min-w-103 max-w-103 items-center justify-between overflow-hidden",
                variant === LicensePrincingCardVariants.Compact &&
                    "min-w-50 max-w-50 px-4 py-8 text-center",
                isCurrent && "border-purple-500 hover:border-purple-700",
            )}
            onClick={() => !disabled && onClick?.()}
            size="md"
            variant="license"
        >
            {isCurrent && (
                <div
                    className={cn(
                        "absolute right-0 top-0 rounded-bl-xl bg-purple-800 px-3 py-1 text-sm font-medium text-white",
                        variant === LicensePrincingCardVariants.Compact &&
                            "py-0.5",
                    )}
                >
                    {t("license.active")}
                </div>
            )}
            <div>
                <CardHeader
                    className={cn(
                        "flex flex-col justify-between",
                        variant === LicensePrincingCardVariants.Compact &&
                            "items-center",
                    )}
                >
                    <CardTitle
                        className={cn(
                            "flex items-center justify-between gap-4 font-medium",
                            LicenseTabs.ENTERPRISE === licenseTab &&
                                "flex-col gap-0",
                        )}
                    >
                        <h2
                            className={cn(
                                "text-xl font-medium",
                                variant ===
                                    LicensePrincingCardVariants.Compact &&
                                    "text-lg",
                                LicenseTabs.ENTERPRISE === licenseTab &&
                                    "text-3xl",
                            )}
                        >
                            {title}
                        </h2>
                        {price &&
                            variant !== LicensePrincingCardVariants.Compact && (
                                <Price
                                    className="text-2xl"
                                    enterprise={
                                        LicenseTabs.ENTERPRISE === licenseTab
                                    }
                                    price={price}
                                />
                            )}
                    </CardTitle>
                    {description && (
                        <CardDescription className="text-md italic">
                            {description}
                        </CardDescription>
                    )}
                </CardHeader>

                <CardContent
                    className={cn(
                        "p-0",
                        variant === LicensePrincingCardVariants.Compact &&
                            "pb-5",
                    )}
                >
                    {price &&
                        variant === LicensePrincingCardVariants.Compact && (
                            <Price
                                enterprise={
                                    LicenseTabs.ENTERPRISE === licenseTab
                                }
                                price={price}
                            />
                        )}
                    {features &&
                    variant === LicensePrincingCardVariants.Compact ? (
                        <Button
                            aria-label={t("license.features.seeFeatures")}
                            className="mt-2 text-sm underline transition-colors duration-200 hover:text-blue-800"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                navigate(routes.selectLicense, {
                                    state: {
                                        defaultTab: licenseTab,
                                    },
                                });
                            }}
                            type="button"
                            variant="invisible"
                        >
                            {t("license.features.seeFeatures")}
                        </Button>
                    ) : (
                        features && (
                            <ul className="mt-4 space-y-2 pb-8 text-sm">
                                {features.map((feature) => (
                                    <li
                                        key={feature}
                                        className="flex items-center gap-2"
                                    >
                                        <div className="h-5 w-5">
                                            <Check className="h-5 w-5 text-green-500" />
                                        </div>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                </CardContent>
            </div>
            <CardFooter className="flex flex-1 items-end justify-center">
                <Button
                    disabled={disabled}
                    gradientBorderVariant="purpleGradient"
                    onClick={onClick}
                    size="gradient"
                    variant="purpleGradient"
                >
                    {buttonText}
                </Button>
            </CardFooter>
        </Card>
    );
};

export default LicensePrincingCard;
